import React, { useMemo } from 'react'
import { extractObjectives, type CanvasObjective } from '../canvas/strategyMapHelpers'
import { slugifyMeasureId, slugifyInitiativeId } from '@utils/slugify'
import './MeasuresInitiativesRegister.css'

interface Props {
  strategyMap: any
}

interface MeasureRow {
  id: string
  name: string
  target: string
  objectiveTitle: string
  perspective: string
}

interface InitiativeRow {
  id: string
  title: string
  objectiveTitle: string
  perspective: string
}

function buildRows(objectives: CanvasObjective[]): {
  measures: MeasureRow[]
  initiatives: InitiativeRow[]
} {
  const measureIds = new Set<string>()
  const initiativeIds = new Set<string>()
  const measures: MeasureRow[] = []
  const initiatives: InitiativeRow[] = []

  for (const obj of objectives) {
    const seenMeasuresInObj = new Set<string>()
    for (const tactic of obj.tactics) {
      initiatives.push({
        id: slugifyInitiativeId(tactic.title, initiativeIds),
        title: tactic.title,
        objectiveTitle: obj.title,
        perspective: obj.perspective,
      })
      for (const metric of tactic.metrics) {
        const dedupKey = metric.id || metric.name
        if (!dedupKey || seenMeasuresInObj.has(dedupKey)) continue
        seenMeasuresInObj.add(dedupKey)
        measures.push({
          id: slugifyMeasureId(metric.name, measureIds),
          name: metric.name,
          target: metric.target?.value ?? '',
          objectiveTitle: obj.title,
          perspective: obj.perspective,
        })
      }
    }
  }

  return { measures, initiatives }
}

export const MeasuresInitiativesRegister: React.FC<Props> = ({ strategyMap }) => {
  const objectives = useMemo(() => extractObjectives(strategyMap), [strategyMap])
  const { measures, initiatives } = useMemo(() => buildRows(objectives), [objectives])

  if (measures.length === 0 && initiatives.length === 0) {
    return (
      <section className="register-section" aria-label="Measures and Initiatives Register">
        <h2>Measures and Initiatives Register</h2>
        <p className="register-empty">No measures or initiatives registered yet.</p>
      </section>
    )
  }

  return (
    <section className="register-section" aria-label="Measures and Initiatives Register">
      <h2>Measures and Initiatives Register</h2>

      {measures.length > 0 && (
        <>
          <h3>Measures</h3>
          <table className="register-table">
            <thead>
              <tr>
                <th scope="col">Measure</th>
                <th scope="col">Target</th>
                <th scope="col">Objective</th>
                <th scope="col">Perspective</th>
              </tr>
            </thead>
            <tbody>
              {measures.map(m => (
                <tr key={m.id} id={m.id}>
                  <td>{m.name}</td>
                  <td>{m.target ? m.target : '—'}</td>
                  <td>{m.objectiveTitle}</td>
                  <td>{m.perspective}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {initiatives.length > 0 && (
        <>
          <h3>Initiatives</h3>
          <table className="register-table">
            <thead>
              <tr>
                <th scope="col">Initiative</th>
                <th scope="col">Objective</th>
                <th scope="col">Perspective</th>
              </tr>
            </thead>
            <tbody>
              {initiatives.map(i => (
                <tr key={i.id} id={i.id}>
                  <td>{i.title}</td>
                  <td>{i.objectiveTitle}</td>
                  <td>{i.perspective}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </section>
  )
}
